import { useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CodeIcon from '@mui/icons-material/Code';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import LinkIcon from '@mui/icons-material/Link';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useTranslation } from 'react-i18next';
import {
    useGetCommissionGroupMembers,
    assignUserCommissionGroup,
    inviteAgentToCommissionGroup,
    removeCommissionGroupMember,
} from '@openapi/commission-groups/commission-groups';
import useNotify from '../../../hooks/useNotify';
import { useCapabilities } from '../../../hooks/useCapabilities';
import { MODAL_TYPE, useModals } from '../../../hooks/useModal';
import SkeletonTable from '../../../components/atom/SkeletonTable';
import AffiliateBookingSnippets, {
    buildSnippets,
    copyToClipboard,
} from './AffiliateBookingSnippets';

function MemberRow({
    member,
    showId,
    venueSlug,
    onResend,
    onRemove,
    onCopy,
    onSetRole,
    busy,
    canManageGroups,
    t,
}) {
    const [open, setOpen] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const hasSnippets = !!member.affiliateCode && !!venueSlug;
    // Mirrors the backend rule: a group manager (no commission_groups.manage)
    // cannot re-invite a manager row - the endpoint rejects it, so don't offer
    // an action that can only error.
    const canResend = canManageGroups || !member.isManager;
    const snippets = hasSnippets
        ? buildSnippets({ venueSlug, showId, affiliateCode: member.affiliateCode })
        : null;

    const closeMenu = () => setMenuAnchor(null);
    const menuAction = (fn) => () => {
        closeMenu();
        fn();
    };

    return (
        <>
            {/* The hidden snippets row below carries its own 4px theme border,
                so this row must drop its own or code rows read as double-thick. */}
            <TableRow className={hasSnippets ? 'removeBorderBottom' : undefined}>
                <TableCell>{member.name}</TableCell>
                <TableCell>{member.email}</TableCell>
                <TableCell>
                    <Chip
                        label={
                            member.isManager
                                ? t('commissionGroups.management.memberColumns.manager')
                                : t('commissionGroups.management.memberColumns.agent')
                        }
                        size="small"
                        color={member.isManager ? 'primary' : 'default'}
                    />
                </TableCell>
                <TableCell>
                    <Stack spacing={0.25} alignItems="flex-start">
                        {member.invitePending ? (
                            <Tooltip title={t('commissionGroups.members.status.pendingTooltip')}>
                                <Chip
                                    label={t('commissionGroups.members.status.pending')}
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                />
                            </Tooltip>
                        ) : (
                            <Chip
                                label={t('commissionGroups.members.status.active')}
                                size="small"
                                color="success"
                                variant="outlined"
                            />
                        )}
                        {member.welcomeLastSentAt && (
                            <Typography variant="caption" color="text.secondary">
                                {t('commissionGroups.members.welcomeSentAt', {
                                    date: new Date(member.welcomeLastSentAt).toLocaleDateString(),
                                })}
                            </Typography>
                        )}
                    </Stack>
                </TableCell>
                <TableCell>
                    {member.affiliateCode ? (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {member.affiliateCode}
                            </Typography>
                            <Tooltip title={t('commissionGroups.assignmentAffiliateCode.copy')}>
                                <IconButton
                                    size="small"
                                    onClick={() => onCopy(member.affiliateCode)}
                                >
                                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            -
                        </Typography>
                    )}
                </TableCell>
                <TableCell align="right">
                    <IconButton
                        size="small"
                        aria-label={t('commissionGroups.members.rowActions')}
                        onClick={(e) => setMenuAnchor(e.currentTarget)}
                        disabled={busy}
                    >
                        <MoreVertIcon fontSize="small" />
                    </IconButton>
                    <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={closeMenu}>
                        {canResend && (
                            <MenuItem onClick={menuAction(() => onResend(member))}>
                                <ListItemIcon>
                                    <EmailIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>
                                    {t('commissionGroups.members.resendWelcome')}
                                </ListItemText>
                            </MenuItem>
                        )}
                        {snippets && (
                            <MenuItem onClick={menuAction(() => onCopy(snippets.link))}>
                                <ListItemIcon>
                                    <LinkIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>
                                    {t('commissionGroups.members.copyBookingLink')}
                                </ListItemText>
                            </MenuItem>
                        )}
                        {snippets && (
                            <MenuItem onClick={menuAction(() => onCopy(snippets.embed))}>
                                <ListItemIcon>
                                    <CodeIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>
                                    {t('commissionGroups.members.copyEmbedCode')}
                                </ListItemText>
                            </MenuItem>
                        )}
                        {hasSnippets && (
                            <MenuItem onClick={menuAction(() => setOpen((o) => !o))}>
                                <ListItemIcon>
                                    {open ? (
                                        <VisibilityOffIcon fontSize="small" />
                                    ) : (
                                        <VisibilityIcon fontSize="small" />
                                    )}
                                </ListItemIcon>
                                <ListItemText>
                                    {open
                                        ? t('commissionGroups.members.hideSnippets')
                                        : t('commissionGroups.members.showSnippets')}
                                </ListItemText>
                            </MenuItem>
                        )}
                        {/* Role changes are staff-only (the backend rejects
                            group-manager callers) and use the assign endpoint's
                            partial upsert, so the affiliate code is untouched.
                            Removing a manager requires demoting first. */}
                        {canManageGroups && !member.isManager && (
                            <MenuItem
                                onClick={menuAction(() => onSetRole(member, { isManager: true }))}
                            >
                                <ListItemIcon>
                                    <SupervisorAccountIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>
                                    {t('commissionGroups.members.makeManager')}
                                </ListItemText>
                            </MenuItem>
                        )}
                        {canManageGroups && member.isManager && (
                            <MenuItem
                                onClick={menuAction(() =>
                                    onSetRole(member, { canEdit: !member.canEdit }),
                                )}
                            >
                                <ListItemIcon>
                                    {member.canEdit ? (
                                        <CheckBoxIcon fontSize="small" />
                                    ) : (
                                        <CheckBoxOutlineBlankIcon fontSize="small" />
                                    )}
                                </ListItemIcon>
                                <ListItemText>
                                    {t('commissionGroups.members.allowBookingEdits')}
                                </ListItemText>
                            </MenuItem>
                        )}
                        {canManageGroups && member.isManager && (
                            <MenuItem
                                onClick={menuAction(() => onSetRole(member, { isManager: false }))}
                            >
                                <ListItemIcon>
                                    <PersonRemoveIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>
                                    {t('commissionGroups.members.revokeManager')}
                                </ListItemText>
                            </MenuItem>
                        )}
                        {!member.isManager && (
                            <MenuItem onClick={menuAction(() => onRemove(member))}>
                                <ListItemIcon>
                                    <DeleteIcon color="error" fontSize="small" />
                                </ListItemIcon>
                                <ListItemText sx={{ color: 'error.main' }}>
                                    {t('commissionGroups.management.removeAgent')}
                                </ListItemText>
                            </MenuItem>
                        )}
                    </Menu>
                </TableCell>
            </TableRow>
            {hasSnippets && (
                <TableRow>
                    <TableCell sx={{ py: 0, borderBottom: open ? undefined : 'none' }} colSpan={6}>
                        <Collapse in={open} unmountOnExit>
                            <Box sx={{ py: 2 }}>
                                <AffiliateBookingSnippets
                                    venueSlug={venueSlug}
                                    showId={showId}
                                    affiliateCode={member.affiliateCode}
                                    variant="compact"
                                />
                            </Box>
                        </Collapse>
                    </TableCell>
                </TableRow>
            )}
        </>
    );
}

/**
 * Members of one commission group, with each agent's affiliate code and booking
 * snippets, plus invite/resend/remove. Shared by the commission group settings
 * page and the affiliate manager console so onboarding lives wherever the group
 * does, rather than being split across the users page and a second console.
 */
export default function CommissionGroupMembers({ commissionGroupId }) {
    const { t } = useTranslation();
    const { notifySuccess, notifyError } = useNotify();
    const { can, CAPABILITIES } = useCapabilities();
    const canManageGroups = can(CAPABILITIES.COMMISSION_GROUPS_MANAGE);
    const { showModal } = useModals();

    const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
    const [inviteName, setInviteName] = useState('');
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteAsManager, setInviteAsManager] = useState(false);
    const [inviteWithCode, setInviteWithCode] = useState(false);
    // One entry per in-flight row action, so acting on a second member does
    // not re-enable the first row's buttons mid-flight.
    const [busyIds, setBusyIds] = useState(() => new Set());
    const [inviting, setInviting] = useState(false);

    const setRowBusy = (assignmentId, isBusy) =>
        setBusyIds((prev) => {
            const next = new Set(prev);
            if (isBusy) {
                next.add(assignmentId);
            } else {
                next.delete(assignmentId);
            }
            return next;
        });

    const {
        data,
        isLoading,
        refetch: refetchMembers,
    } = useGetCommissionGroupMembers(commissionGroupId, {
        query: { enabled: !!commissionGroupId, onError: notifyError },
    });

    const members = data?.members ?? [];
    const showId = data?.showId;
    const venueSlug = data?.venueSlug;

    const closeInviteDialog = () => {
        setInviteDialogOpen(false);
        setInviteName('');
        setInviteEmail('');
        setInviteAsManager(false);
        setInviteWithCode(false);
    };

    const handleInvite = async () => {
        if (!inviteName.trim() || !inviteEmail.trim()) return;
        setInviting(true);
        try {
            await inviteAgentToCommissionGroup(commissionGroupId, {
                name: inviteName.trim(),
                email: inviteEmail.trim(),
                // Both flags are staff-only: the backend rejects isManager
                // from group managers and never mints a code for them.
                ...(canManageGroups
                    ? {
                          withAffiliateCode: inviteWithCode,
                          ...(inviteAsManager ? { isManager: true } : {}),
                      }
                    : {}),
            });
            notifySuccess(t('commissionGroups.notifications.agentInvited'));
            closeInviteDialog();
        } catch (err) {
            // Surface the backend error directly - e.g. "User is already assigned
            // to another commission group" - instead of a vague generic.
            notifyError(err);
        } finally {
            // Refetch on error too: the backend commits the member before
            // sending the email, so a failed send still added a row.
            refetchMembers();
            setInviting(false);
        }
    };

    // Re-inviting an existing member resends their welcome email rather than
    // creating a second assignment, so resend needs no endpoint of its own.
    const handleResend = async (member) => {
        setRowBusy(member.assignmentId, true);
        try {
            await inviteAgentToCommissionGroup(commissionGroupId, {
                name: member.name,
                email: member.email,
            });
            notifySuccess(t('commissionGroups.notifications.welcomeResent'));
        } catch (err) {
            notifyError(err);
        } finally {
            // Refetch on error too, so the welcome-sent timestamp stays honest.
            refetchMembers();
            setRowBusy(member.assignmentId, false);
        }
    };

    const handleRemove = async (member) => {
        setRowBusy(member.assignmentId, true);
        try {
            await removeCommissionGroupMember(commissionGroupId, member.assignmentId);
            notifySuccess(t('commissionGroups.notifications.agentRemoved'));
            refetchMembers();
        } catch (err) {
            notifyError(err);
        } finally {
            setRowBusy(member.assignmentId, false);
        }
    };

    const confirmRemove = (member) =>
        showModal(MODAL_TYPE.CONFIRMATION, {
            title: t('confirmationModal.title'),
            content: t('confirmationModal.content'),
            confirm: () => handleRemove(member),
        });

    const handleCopy = async (text) => {
        await copyToClipboard(text);
        notifySuccess(t('commissionGroups.members.copied'));
    };

    // Promote/demote and the can_edit opt-in ride the assign endpoint's
    // partial upsert: omitted fields (incl. the affiliate code) keep their
    // stored values, and the backend clears can_edit on demote.
    const handleSetRole = async (member, changes) => {
        setRowBusy(member.assignmentId, true);
        try {
            await assignUserCommissionGroup({
                userId: member.userId,
                commissionGroupId,
                ...changes,
            });
            notifySuccess(t('commissionGroups.members.roleUpdated'));
        } catch (err) {
            notifyError(err);
        } finally {
            refetchMembers();
            setRowBusy(member.assignmentId, false);
        }
    };

    return (
        <Box sx={{ width: '100%', textAlign: 'left' }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    mb: 1,
                    flexWrap: 'wrap',
                }}
            >
                <Typography variant="subtitle1">{t('commissionGroups.members.title')}</Typography>
                <Button
                    variant="contained"
                    size="small"
                    startIcon={<PersonAddIcon />}
                    onClick={() => setInviteDialogOpen(true)}
                    disabled={!commissionGroupId}
                >
                    {t('commissionGroups.management.inviteAgent')}
                </Button>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('commissionGroups.members.description')}
            </Typography>

            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                {t('commissionGroups.management.memberColumns.name')}
                            </TableCell>
                            <TableCell>
                                {t('commissionGroups.management.memberColumns.email')}
                            </TableCell>
                            <TableCell>
                                {t('commissionGroups.management.memberColumns.role')}
                            </TableCell>
                            <TableCell>{t('commissionGroups.members.status.column')}</TableCell>
                            <TableCell>{t('commissionGroups.members.affiliateCode')}</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            <SkeletonTable rows={3} cells={6} />
                        ) : members.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    <Typography color="text.secondary">
                                        {t('commissionGroups.members.emptyState')}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            members.map((member) => (
                                <MemberRow
                                    key={member.assignmentId}
                                    member={member}
                                    showId={showId}
                                    venueSlug={venueSlug}
                                    onResend={handleResend}
                                    onRemove={confirmRemove}
                                    onCopy={handleCopy}
                                    onSetRole={handleSetRole}
                                    busy={busyIds.has(member.assignmentId)}
                                    canManageGroups={canManageGroups}
                                    t={t}
                                />
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={inviteDialogOpen} onClose={closeInviteDialog} maxWidth="xs" fullWidth>
                <DialogTitle>{t('commissionGroups.management.inviteAgentTitle')}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {t('commissionGroups.members.inviteHelp')}
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        label={t('commissionGroups.management.inviteAgentName')}
                        value={inviteName}
                        onChange={(e) => setInviteName(e.target.value)}
                        sx={{ mt: 2 }}
                    />
                    <TextField
                        fullWidth
                        type="email"
                        label={t('commissionGroups.management.inviteAgentEmail')}
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleInvite()}
                        sx={{ mt: 2 }}
                    />
                    {canManageGroups && (
                        <FormControlLabel
                            sx={{ mt: 1, display: 'flex' }}
                            control={
                                <Checkbox
                                    checked={inviteWithCode}
                                    onChange={(e) => setInviteWithCode(e.target.checked)}
                                />
                            }
                            label={t('commissionGroups.members.inviteWithCode')}
                        />
                    )}
                    {canManageGroups && (
                        <FormControlLabel
                            sx={{ display: 'flex' }}
                            control={
                                <Checkbox
                                    checked={inviteAsManager}
                                    onChange={(e) => setInviteAsManager(e.target.checked)}
                                />
                            }
                            label={t('commissionGroups.members.inviteAsManager')}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeInviteDialog}>{t('common.cancel')}</Button>
                    <Button
                        variant="contained"
                        onClick={handleInvite}
                        disabled={inviting || !inviteName.trim() || !inviteEmail.trim()}
                    >
                        {inviting ? (
                            <CircularProgress size={20} />
                        ) : (
                            t('commissionGroups.management.inviteAgentSubmit')
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
